<template>
  <div class="face-scanner">
    <template v-if="!value">
      <video v-show="cameraOn" ref="faceVideo" class="face-media" :class="{ mirrored: mirror }" autoplay playsinline muted></video>
      <div v-if="!cameraOn" class="face-placeholder">
        <i class="ni ni-camera-compact"></i>
        <span>Камера выключена</span>
      </div>
      <p v-if="cameraError" class="face-error">{{ cameraError }}</p>
      <div class="face-actions">
        <base-button v-if="!cameraOn" type="primary" :disabled="processing" @click="startCamera">Включить камеру</base-button>
        <base-button v-else type="primary" :disabled="processing" @click="capturePhoto">Сканировать лицо</base-button>
        <span v-if="processing">Подготовка фото…</span>
      </div>
      <label v-if="cameraOn" class="mirror-toggle">
        <input type="checkbox" v-model="mirror"> Зеркальное отображение
      </label>
    </template>
    <template v-else>
      <img :src="preview" class="face-media" alt="capture" />
      <div class="face-actions">
        <base-button type="secondary" @click="retake">Переснять</base-button>
        <span class="face-ok"><i class="ni ni-check-bold"></i> Снято</span>
      </div>
    </template>

    <div class="device-toggle" @click="showManual = !showManual">
      <i :class="showManual ? 'ni ni-bold-up' : 'ni ni-bold-down'"></i>
      Вставить Base64 вручную
    </div>
    <textarea v-show="showManual" class="form-control code-area" rows="3"
              :value="manualValue" @input="onManual($event.target.value)"
              placeholder="Base64 без префикса data:image/…"></textarea>
    <base-button v-if="showManual" type="secondary" :disabled="processing || !manualValue.trim()" @click="preparePhoto(manualValue)">
      Подготовить фото
    </base-button>
    <p class="face-note">Кадр 960×1280. Зеркальное отображение меняет только предпросмотр.</p>

    <canvas ref="faceCanvas" class="face-canvas"></canvas>
  </div>
</template>

<script>
import BaseButton from '@/components/BaseButton.vue';
import axios from 'axios';
import { captureInfinFaceFrame, normalizeInfinFacePhoto, facePhotoError } from '@/utils/infinFacePhoto';

export default {
  name: 'FaceScanner',
  components: { BaseButton },
  props: {
    // v-model: the clean Base64 (no data: prefix) that will be sent to the bank
    value: { type: String, default: '' },
    providerId: { type: Number, required: true },
  },
  data() {
    return { cameraOn: false, cameraError: '', stream: null, mirror: true, showManual: false,
      processing: false, manualValue: '', job: 0, cameraJob: 0, disposed: false, cancelPhoto: null };
  },
  computed: {
    preview() { return this.value ? ('data:image/jpeg;base64,' + this.value) : ''; },
  },
  beforeDestroy() { this.disposed = true; this.cancelPreparation(); this.stopCamera(); },
  methods: {
    async startCamera() {
      if (this.processing || this.disposed) return;
      this.cameraError = '';
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.cameraError = 'Камера недоступна в этом браузере';
        return;
      }
      this.stopCamera();
      const job = ++this.cameraJob;
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user', width: { ideal: 960 }, height: { ideal: 1280 }, aspectRatio: { ideal: 0.75 } },
          audio: false,
        });
        if (this.disposed || job !== this.cameraJob) { stream.getTracks().forEach((t) => t.stop()); return; }
        this.stream = stream;
        this.cameraOn = true;
        this.$nextTick(() => { if (this.$refs.faceVideo) this.$refs.faceVideo.srcObject = this.stream; });
      } catch (e) {
        if (this.disposed || job !== this.cameraJob) return;
        this.cameraError = (e && e.name === 'NotAllowedError') ? 'Доступ к камере запрещён' : 'Не удалось включить камеру';
      }
    },
    async capturePhoto() {
      const video = this.$refs.faceVideo;
      const canvas = this.$refs.faceCanvas;
      if (!video || !canvas || !video.videoWidth) return;
      try {
        const source = captureInfinFaceFrame(video, canvas);
        this.stopCamera();
        await this.preparePhoto(source);
      } catch (error) { this.cameraError = facePhotoError(error); }
    },
    async preparePhoto(source) {
      this.cancelPreparation();
      const job = this.job;
      const cancel = axios.CancelToken.source();
      this.cancelPhoto = cancel.cancel;
      this.processing = true;
      this.cameraError = '';
      this.$emit('busy', true);
      this.stopCamera();
      try {
        const photo = await normalizeInfinFacePhoto(source, this.providerId, { cancelToken: cancel.token });
        if (!this.disposed && job === this.job) this.$emit('input', photo);
      } catch (error) {
        if (!axios.isCancel(error) && !this.disposed && job === this.job) this.cameraError = facePhotoError(error);
      } finally {
        if (!this.disposed && job === this.job) { this.processing = false; this.cancelPhoto = null; this.$emit('busy', false); }
      }
    },
    cancelPreparation() {
      this.job++;
      if (this.cancelPhoto) this.cancelPhoto('capture replaced');
      this.cancelPhoto = null;
      this.processing = false;
      this.$emit('busy', false);
    },
    retake() { this.cancelPreparation(); this.$emit('input', ''); this.manualValue = ''; this.startCamera(); },
    onManual(v) { this.cancelPreparation(); this.manualValue = v || ''; this.$emit('input', ''); },
    stopCamera() {
      this.cameraJob++;
      if (this.stream) { this.stream.getTracks().forEach((t) => t.stop()); this.stream = null; }
      this.cameraOn = false;
    },
  },
};
</script>

<style scoped>
.face-scanner { text-align: center; margin-bottom: 0.75rem; }
.face-media { width: 240px; max-width: 100%; height: 320px; object-fit: cover; border-radius: 0.5rem; background: #1f2435; }
.face-media.mirrored { transform: scaleX(-1); }
.face-placeholder { width: 240px; max-width: 100%; height: 320px; margin: 0 auto; border-radius: 0.5rem; background: #f7fafc; border: 1px dashed #ced4da; display: flex; flex-direction: column; align-items: center; justify-content: center; color: #8898aa; gap: 0.4rem; }
.face-placeholder i { font-size: 2rem; }
.face-error { color: #f5365c; font-size: 0.78rem; margin: 0.4rem 0 0; }
.face-note { color: #8898aa; font-size: 0.75rem; margin-top: 0.5rem; }
.face-actions { display: flex; align-items: center; justify-content: center; gap: 0.75rem; margin-top: 0.6rem; }
.face-ok { color: #2dce89; font-size: 0.85rem; }
.mirror-toggle { display: inline-flex; align-items: center; gap: 0.4rem; font-size: 0.8rem; color: #525f7f; margin-top: 0.5rem; cursor: pointer; }
.mirror-toggle input { margin: 0; }
.device-toggle { cursor: pointer; color: #5e72e4; font-size: 0.85rem; margin: 0.5rem 0; user-select: none; }
.code-area { width: 100%; font-family: monospace; font-size: 0.8rem; }
.face-canvas { display: none; }
</style>
